import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { ArrowUp, Mic, ChevronRight, CornerDownLeft, Plus, Trash2 } from 'react-feather';
import { useAuth } from '../hooks/useAuth';
import useF8Chat from '../hooks/useF8Chat.jsx';
import { useCardActionHandler } from '../hooks/useCardActionHandler';
import { useVoiceChat } from '../hooks/useVoiceChat';
import F8Card from '../components/F8/cards/F8Card';
import { getPplCurrentlyOnline, chatTopicSuggestionsEurW } from '../firebaseConfig';
import WebVoiceMode from '../components/WebVoiceMode';
import { autoScrollMode } from '../utils/chatAutoScroll';
import RoomChatRail from '../components/RoomChatRail';
import RoomEntryCards from '../components/RoomEntryCards';
import HomeDesk from '../components/Home/HomeDesk';
import { isHomeDeskEnabled, isHomeDeskEnabledSync } from '../services/featureFlags';
import '../styles/F8Chat.css';

const STARTER_PROMPTS = [
  "Who's online right now?",
  "Help me make a new friend",
  "Messaging feels overwhelming",
  "Who should I check in on?",
];

export default function F8Chat() {
  const { user } = useAuth();
  const userId = user?.uid;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    threads, currentThreadId, chatMode, messages, isLoading,
    filteredMentionUsers, showMentions,
    hasMoreOlderMessages, isLoadingOlderMessages,
    loadThreadMessages, deleteThread, sendMessage, loadOlderMessages,
    resetChat, handleMentionDetection, handleMentionSelect,
    renderTextWithMentions, formatTimestamp,
  } = useF8Chat({ userId, isAnonymous: false });

  const [inputText, setInputText] = useState('');
  const [onlineCount, setOnlineCount] = useState(null);
  const [voiceOpen, setVoiceOpen] = useState(false);
  // Which thread the running (or just-ended) voice session wrote to — the
  // provider is hoisted to the app root, so this survives closing the overlay.
  const { voiceThreadId } = useVoiceChat();
  const [suggestedTopics, setSuggestedTopics] = useState([]);
  const [isLoadingTopics, setIsLoadingTopics] = useState(false);
  const [topicsLoaded, setTopicsLoaded] = useState(false);
  const chatContainerRef = useRef(null);
  const inputRef = useRef(null);
  // PPL-546 — the thread `?thread=` has already been honoured, so a second
  // render before the param is stripped cannot load it twice.
  const openedFromQueryRef = useRef(null);

  // PPL-416 — the desk replaces the initial block when `homeDesk` is on (or
  // the uid is allow-listed). Seeded from the session's already-read flags,
  // else the last resolved answer kept in localStorage, so neither a remount
  // nor a reload flashes the old home first; `null` (never resolved, or a
  // read failure) renders the current home exactly as it is.
  const [deskEnabled, setDeskEnabled] = useState(() => isHomeDeskEnabledSync(userId));
  useEffect(() => {
    let alive = true;
    setDeskEnabled(isHomeDeskEnabledSync(userId));
    isHomeDeskEnabled(userId).then((enabled) => {
      if (alive) setDeskEnabled(enabled);
    });
    return () => {
      alive = false;
    };
  }, [userId]);
  const useDesk = deskEnabled === true;

  const canSend = inputText.trim().length > 0;

  const handleCardAction = useCardActionHandler({
    onSkillInvoke: (message) => {
      if (!user || !userId) {
        navigate('/login', { state: { from: location }, replace: true });
        return;
      }
      sendMessage(message);
    },
  });

  useEffect(() => {
    // Only probe the online count once authenticated. /f8chat is a public
    // route, and getPplCurrentlyOnline requires auth — calling it while
    // anonymous throws "unauthenticated" and floods the function logs with 500s.
    // On the desk path the online signal is M8's sub-line (W-H2): no probe.
    if (!userId || useDesk) return;
    getPplCurrentlyOnline({})
      .then((result) => {
        const count = result?.data?.users?.length ?? 0;
        setOnlineCount(count);
      })
      .catch(() => {
        setOnlineCount(0);
      });
  }, [userId, useDesk]);

  useEffect(() => {
    // The desk's prompts come with its payload; the starter grid is the
    // flag-off path only.
    if (!userId || useDesk || topicsLoaded || isLoadingTopics || chatMode !== 'initial') return;
    setIsLoadingTopics(true);
    chatTopicSuggestionsEurW({ screenContext: 'ChatScreen' })
      .then((result) => {
        if (result?.data?.success && Array.isArray(result.data.suggestions) && result.data.suggestions.length > 0) {
          setSuggestedTopics(result.data.suggestions);
        }
        setTopicsLoaded(true);
      })
      .catch(() => {
        setTopicsLoaded(true);
      })
      .finally(() => {
        setIsLoadingTopics(false);
      });
  }, [userId, useDesk, chatMode, topicsLoaded, isLoadingTopics]);

  // Open a specific thread when navigated with state.threadId (from HistoryDrawer)
  useEffect(() => {
    const threadId = location.state?.threadId;
    if (!threadId || !userId) return;
    if (currentThreadId === threadId) return;
    loadThreadMessages(threadId);
    // Clear state so refresh doesn't re-open the same thread.
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, userId, currentThreadId, loadThreadMessages, navigate, location.pathname]);

  /**
   * PPL-546 — open a thread named by `?thread=<id>`. This is the destination a
   * channel-reply notification routes to (`services/notificationRoutes`): the
   * reply f8 wrote lives on the link's pinned thread, and a tap has to land on
   * it rather than on a fresh chat.
   *
   * A link is the one entry that survives leaving the app, so it cannot use
   * `location.state` like the drawer above does.
   *
   * The param is only honoured once signed in, and is left in the URL until
   * then ON PURPOSE: `useAuth` resolves asynchronously, so the first render of
   * a perfectly ordinary signed-in reload has no uid yet. Stripping it there
   * would break every deep link. While signed out /f8chat renders its
   * login hint, and the param is still in the URL if auth arrives in place.
   */
  useEffect(() => {
    const threadId = searchParams.get('thread');
    if (!threadId || !userId) return;
    if (openedFromQueryRef.current !== threadId) {
      openedFromQueryRef.current = threadId;
      if (currentThreadId !== threadId) loadThreadMessages(threadId);
    }
    // Drop just this param, keeping any other, so a refresh does not re-open
    // the thread over whatever the user moved on to.
    const next = new URLSearchParams(searchParams);
    next.delete('thread');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, userId, currentThreadId, loadThreadMessages]);

  // Start a fresh chat when navigated with state.newChat (left rail / mobile drawer button)
  useEffect(() => {
    if (!location.state?.newChat) return;
    resetChat();
    setInputText('');
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, resetChat, navigate, location.pathname]);

  // Open voice mode when a card action navigates here with state.startVoice
  // (voice launcher card's "Start voice chat" button).
  useEffect(() => {
    if (!location.state?.startVoice) return;
    setVoiceOpen(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, navigate, location.pathname]);

  // Prefill the composer when navigated with state.initialMessage — the
  // person page's "ask f8 about X" entry (PPL-265). Prefill, don't auto-send:
  // the user should see what they're about to say. Mirrors the app, where
  // ChatScreen receives initialMessage the same way.
  useEffect(() => {
    const initial = location.state?.initialMessage;
    if (typeof initial !== 'string' || !initial) return;
    setInputText(initial);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, navigate, location.pathname]);

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  // PPL-534 — bring the TOP of the newest message to the top of the viewport,
  // so a card taller than the viewport is read from its first line. Each row
  // carries its index as a data attribute; if the row cannot be found the
  // position is left alone rather than jumped to the bottom, which is the jump
  // this policy exists to prevent.
  const scrollToNewestMessageTop = () => {
    const container = chatContainerRef.current;
    if (!container) return;
    const row = container.querySelector(`[data-msg-index="${messages.length - 1}"]`);
    if (!row) return;
    // Measured rather than read off offsetTop: the row's offsetParent is not
    // necessarily the scroll container, so rects + the current scrollTop are
    // the only positioning-independent way to get there.
    const delta = row.getBoundingClientRect().top - container.getBoundingClientRect().top;
    container.scrollTop = Math.max(0, container.scrollTop + delta);
  };

  /**
   * PPL-534 — leaving voice mode opens the thread the call was written to, so
   * the card the user was looking at is there as a persisted bot message (the
   * proxy writes every skill turn — summary + cards — onto the thread). When
   * the session resumed the thread already in view, the live snapshot listener
   * in useF8Chat has already appended those messages.
   */
  const handleVoiceClose = () => {
    setVoiceOpen(false);
    if (voiceThreadId && voiceThreadId !== currentThreadId) {
      loadThreadMessages(voiceThreadId);
    }
  };

  /**
   * PPL-534 — every auto-scroll goes through the policy in
   * ../utils/chatAutoScroll so a message carrying skill cards is not scrolled
   * past. `message` defaults to the newest one in the list.
   */
  const autoScroll = (trigger, { delay = 0, message } = {}) => {
    const newest = message !== undefined ? message : messages[messages.length - 1];
    const mode = autoScrollMode(trigger, newest);
    if (mode === 'skip') return;
    const run = mode === 'message_top' ? scrollToNewestMessageTop : scrollToBottom;
    if (delay > 0) setTimeout(run, delay);
    else run();
  };

  useEffect(() => {
    if (messages.length > 0) {
      autoScroll('message_arrived', { delay: 100 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, isLoading]);

  const handleInputChange = (e) => {
    const text = e.target.value;
    setInputText(text);
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = inputRef.current.scrollHeight + 'px';
    }
    handleMentionDetection(text, e.target.selectionStart);
  };

  const onMentionSelect = (mentionUser) => {
    const cursorPos = inputRef.current?.selectionStart || inputText.length;
    const { newText, newCursorPos } = handleMentionSelect(mentionUser, inputText, cursorPos);
    setInputText(newText);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
        inputRef.current.focus();
      }
    }, 0);
  };

  const handleSend = async (text = inputText) => {
    if (!text.trim()) return;
    if (!user || !userId) {
      // "sending your first message signs you in": the draft rides along
      // and Login hands it back as state.initialMessage (the same entry the
      // person page uses), so it lands in the composer after sign-in.
      navigate('/login', { state: { from: location, initialMessage: text }, replace: true });
      return;
    }
    setInputText('');
    await sendMessage(text);
    autoScroll('composer', { delay: 500 });
  };

  // PPL-416 — the desk's composer hooks. Prefill never sends: the user sees
  // what they are about to say. The textarea is the existing one.
  const prefillComposer = (text) => {
    setInputText(text);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = el.scrollHeight + 'px';
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleReset = () => {
    resetChat();
    setInputText('');
  };

  return (
      <div className="f8-chat-shell">
            {chatMode === 'initial' && useDesk ? (
              /* ── Home state · the desk (PPL-416, flag `homeDesk`) ── */
              <HomeDesk
                uid={userId ?? null}
                chats={threads.length}
                onPrefill={prefillComposer}
                onSend={handleSend}
                onOpenThread={loadThreadMessages}
              />
            ) : chatMode === 'initial' ? (
              /* ── Empty / Home state (flag off — unchanged) ── */
              <div className="f8-home">
                <div className="f8-home-inner">
                  {/* Status pill */}
                  <div className="f8-status-pill">
                    <span className="f8-orb" />
                    <span className="f8-status-label">f8 · ready</span>
                  </div>

                  {/* Hero */}
                  <h1 className="f8-hero-title">
                    What's on{' '}
                    <span className="f8-gradient-text">your mind?</span>
                  </h1>
                  <p className="f8-hero-subtitle">
                    Ask f8 anything about your relationships...
                    {!user && (
                      <span className="f8-login-hint">
                        <Link to="/login" state={{ from: location }} className="f8-login-link">
                          login
                        </Link>{' '}
                        to save your conversations
                      </span>
                    )}
                  </p>

                  {/* Room entry CTA cards — both deep-link into the rooms
                      composer (PPL-133). */}
                  <RoomEntryCards />

                  {/* Active mediation chips — only visible when the user has
                      pending or ongoing mediations. Returns null otherwise. */}
                  {user && <RoomChatRail />}

                  {/* 2x2 starter grid */}
                  <div className="f8-starter-grid">
                    {(suggestedTopics.length > 0 ? suggestedTopics : STARTER_PROMPTS).map((p, i) => (
                      <button
                        key={i}
                        className="f8-starter-btn"
                        onClick={() => handleSend(p)}
                      >
                        <span>{p}</span>
                        <CornerDownLeft size={12} className="f8-starter-icon" />
                      </button>
                    ))}
                  </div>

                  {/* Online hint */}
                  {onlineCount > 0 && (
                    <div className="f8-online-hint">
                      <span className="f8-online-count">{onlineCount} {onlineCount === 1 ? 'person' : 'people'}</span> online ·{' '}
                      <button
                        className="f8-show-me"
                        onClick={() => handleSend("Who's online in my circles?")}
                      >
                        show me
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* ── Conversation state ─────────────────────── */
              <div className="f8-conversation">
                {/* Header */}
                <div className="f8-conv-header">
                  <div className="f8-conv-header-left">
                    <span className="f8-orb f8-orb-sm" />
                    <span className="f8-conv-title">f8</span>
                  </div>
                  <button className="f8-new-chat-btn" onClick={handleReset} title="new chat">
                    <Plus size={16} />
                  </button>
                </div>

                {/* Messages */}
                <div className="f8-messages" ref={chatContainerRef}>
                  {hasMoreOlderMessages && messages.length >= 20 && (
                    <div className="f8-load-older">
                      <button onClick={loadOlderMessages} disabled={isLoadingOlderMessages} className="f8-load-older-btn">
                        {isLoadingOlderMessages ? 'loading...' : 'load older messages'}
                      </button>
                    </div>
                  )}

                  <div className="f8-messages-inner">
                    {messages.map((message, index) => (
                      <div key={message.id} className="f8-msg-group" data-msg-index={index}>
                        {message.sender === 'user' ? (
                          <div className="f8-user-bubble">
                            {renderTextWithMentions(
                              message.text,
                              'f8-msg-text',
                              'f8-mention f8-mention-user'
                            )}
                          </div>
                        ) : (
                          <div className="f8-agent-wrap">
                            <div className="f8-agent-bubble-border">
                              <div className="f8-agent-bubble">
                                {renderTextWithMentions(
                                  message.text,
                                  'f8-msg-text',
                                  'f8-mention f8-mention-bot'
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Cards */}
                        {message.sender === 'bot' &&
                         Array.isArray(message.cards) &&
                         message.cards.length > 0 && (
                          <div className="f8-cards">
                            {message.cards
                              .filter((card) => card && typeof card === 'object' && card.type)
                              .map((card, idx) => (
                                <F8Card key={idx} card={card} onAction={handleCardAction} />
                              ))}
                          </div>
                        )}

                        {/* Followup chips */}
                        {message.sender === 'bot' &&
                         message.suggested_responses &&
                         message.suggested_responses.length > 0 &&
                         index === messages.length - 1 && (
                          <div className="f8-followups">
                            {message.suggested_responses.map((response, idx) => (
                              <button
                                key={idx}
                                className="f8-followup-chip"
                                onClick={() => handleSend(response)}
                              >
                                <span>{response}</span>
                                <ChevronRight size={12} className="f8-followup-arrow" />
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Loading */}
                    {isLoading && (
                      <div className="f8-agent-wrap">
                        <div className="f8-agent-bubble-border">
                          <div className="f8-agent-bubble f8-loading-bubble">
                            <span className="f8-loading-label">Thinking</span>
                            <div className="f8-loading-dots">
                              <span /><span /><span />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ── Composer (always visible) ──────────────── */}
            <div className="f8-composer-area">
              <div className={`f8-composer-border ${canSend ? 'active' : ''}`}>
                <div className="f8-composer-inner">
                  <div className="f8-composer-top">
                    <textarea
                      ref={inputRef}
                      className="f8-textarea"
                      placeholder={user ? "Ask f8 anything... (use @ to mention someone)" : "login to start chatting..."}
                      value={inputText}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyPress}
                      rows={1}
                      maxLength={250}
                    />
                    <span className="f8-char-count">{inputText.length}/250</span>
                  </div>

                  {/* Mentions dropdown */}
                  {showMentions && filteredMentionUsers.length > 0 && (
                    <div className="f8-mentions-dropdown">
                      {filteredMentionUsers.map((mentionUser) => (
                        <button
                          key={mentionUser.id}
                          className="f8-mention-item"
                          onClick={() => onMentionSelect(mentionUser)}
                        >
                          <div className="f8-mention-avatar">
                            {mentionUser.name ? mentionUser.name.charAt(0).toUpperCase() : "?"}
                          </div>
                          <span className="f8-mention-name">{mentionUser.name}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="f8-composer-bottom">
                    <div className="f8-composer-right">
                      <span className="f8-hint">
                        f8 may make mistakes · <kbd className="f8-kbd">↵</kbd> to send
                      </span>
                      <button className="f8-mic-btn" title="Voice" onClick={() => setVoiceOpen(true)}>
                        <Mic size={16} />
                      </button>
                      <button
                        className={`f8-send-btn ${canSend ? 'active' : ''}`}
                        onClick={() => handleSend()}
                        disabled={!canSend || isLoading}
                        title={!user ? "login required" : "send message"}
                      >
                        <ArrowUp size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          <WebVoiceMode
            open={voiceOpen}
            onClose={handleVoiceClose}
            chatHistory={messages}
            threadId={currentThreadId}
          />
          </div>
  );
}
